import React from 'react';
import { useHasFF } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { blockMaxSubjects } from 'venn-state';
import StyledEmptyState from '../StyledEmptyState';
import { useAnalysis } from '../../logic/useAnalysis';
import { useSelectedMetrics } from '../../logic/useSelectedMetrics';
import { getAppTitle } from 'venn-ui-kit';
import { StyledErrorState } from '../StyledErrorState';
import type { ErrorWrapper } from './errorWrapperTypes';
import { SupportedErrorCodes, type Message } from 'venn-api';
import { useMetricsError } from '../../logic/useMetricsError';
import { Match } from 'effect';

const ERROR_CODES_SOLVABLE_BY_PROXY = SupportedErrorCodes
  ? [
      // Until we have a better way for users to debug WHICH investments are the ones causing issues, we don't want to tell
      // them to proxy SupportedErrorCodes.NoCommonOverlap
      // SupportedErrorCodes.NoCommonOverlap,
      SupportedErrorCodes.NoOverlapWithFactorReturns,
      SupportedErrorCodes.InsufficientReturns,
      SupportedErrorCodes.InsufficientHistoryForDrawdown,
      SupportedErrorCodes.InsufficientDataForScenario,
      SupportedErrorCodes.PeriodTooNarrowForFactorTrend,
    ]
  : [];

export const MetricsErrorWrapper: ErrorWrapper = ({ blockId, children }) => {
  const maybeError = useMetricsError({ blockId });
  const hasBulkProxyFf = useHasFF('portfolio_bulk_proxy_ff');
  const maxSubjects = useRecoilValue(blockMaxSubjects(blockId));

  const { analyses } = useAnalysis(blockId);

  const selectedMetrics = useSelectedMetrics();

  const allMetricsHaveErrorsMessage =
    maybeError === 'allMetricsHaveErrors'
      ? analyses
          .slice(0, maxSubjects)
          .flatMap((analysisResult) => {
            const analysis = analysisResult.find(
              (analysis) => analysis?.analysisType === selectedMetrics[0]?.analysisType,
            );
            return analysis?.message ?? analysis?.globalError;
          })
          .find((m) => !!m)
      : undefined;

  return Match.value(maybeError).pipe(
    Match.when('allMetricsHaveTransientErrors', () => (
      <StyledEmptyState
        selectedRefId={blockId}
        header="Temporarily Unable to Run Analysis"
        message={`Please try again later or contact ${getAppTitle()} Support`}
      />
    )),
    Match.when('allMetricsHaveErrors', () => (
      <StyledEmptyState
        selectedRefId={blockId}
        header="Unable to run analysis"
        message={getFinalText(hasBulkProxyFf, allMetricsHaveErrorsMessage)}
      />
    )),
    Match.when('moreThanOneMetricSelected', () => (
      <StyledErrorState
        selectedRefId={blockId}
        header="Charting is only available with 1 metric selected"
        message="Please select only 1 metric"
      />
    )),
    Match.when(undefined, () => children),
    Match.exhaustive,
  );
};

function getFinalText(hasBulkProxyFf: boolean, msg: Message | undefined): string {
  const genericError = 'Please check the block inputs and configuration';
  if (!msg?.text) {
    return genericError;
  }
  if (hasBulkProxyFf && ERROR_CODES_SOLVABLE_BY_PROXY.includes(msg.code)) {
    const trimmedText = msg.text.trim();
    const trimmedTextWithPeriod = trimmedText.endsWith('.') ? trimmedText : `${trimmedText}.`;
    return `${trimmedTextWithPeriod} Go to Manage Data to add proxies.`;
  }

  if (Object.values(SupportedErrorCodes).includes(msg.code)) {
    return msg.text;
  }

  return genericError;
}
